import type { AxiosError } from 'axios';

/** One advisory rule that fired (C8), as the command refusal reports it. */
export interface RuleWarning {
  ruleKey: string;
  message: string;
}

const ACKNOWLEDGEMENT_CODE_SUFFIX = 'WarningsRequireAcknowledgement';

/**
 * The warnings a refused command wants acknowledged (C8), or null when the error is
 * anything else. The server refuses BEFORE saving with `Nexo:Rules:WarningsRequireAcknowledgement`
 * and lists the fired warnings in `error.data.warnings`; the caller shows them, the user
 * accepts, and the same command is resent with `acknowledgedWarnings: [ruleKey, …]`.
 */
export function getWarningsRequiringAcknowledgement(error: unknown): RuleWarning[] | null {
  const errorData = (error as AxiosError<{ error?: RuleWarningEnvelope }>)?.response?.data?.error;
  return getEnvelopeWarnings(errorData);
}

/** The part of an ABP error envelope a warnings refusal is read from. */
export interface RuleWarningEnvelope {
  code?: string | null;
  data?: Record<string, unknown> | null;
}

/**
 * The warnings one error envelope asks to have acknowledged, or null when it is any other refusal — the
 * envelope of a failed request, or of one row of a bulk edit's outcome (Bulk-Edit-And-Actions.md D14).
 */
export function getEnvelopeWarnings(errorData: RuleWarningEnvelope | null | undefined): RuleWarning[] | null {
  if (!errorData?.code?.endsWith(ACKNOWLEDGEMENT_CODE_SUFFIX)) {
    return null;
  }

  const raw = errorData.data?.warnings;
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .filter((w): w is Record<string, unknown> => typeof w === 'object' && w !== null)
    .map((w) => ({ ruleKey: String(w.ruleKey ?? ''), message: String(w.message ?? '') }))
    .filter((w) => w.ruleKey.length > 0);
}

/** The keys to send back as the acknowledgement. */
export function acknowledgementFor(warnings: RuleWarning[]): string[] {
  return warnings.map((w) => w.ruleKey);
}

/**
 * The acknowledgement for a resubmission, carrying forward what an earlier round already
 * accepted. The server names only the warnings still unacknowledged, so a resubmission
 * listing just those would drop the earlier keys and be refused again — the two rounds
 * would trade refusals forever. Accumulate instead.
 */
export function acknowledgementWith(
  alreadyAcknowledged: unknown,
  warnings: RuleWarning[],
): string[] {
  const previous = Array.isArray(alreadyAcknowledged)
    ? alreadyAcknowledged.filter((key): key is string => typeof key === 'string')
    : [];
  return Array.from(new Set([...previous, ...acknowledgementFor(warnings)]));
}
